/**
 * Shared review + sourcing metadata for the editorial pages.
 *
 * Every guide, question, state, county and easement-type page renders a
 * "last reviewed" date and a sourcing line from here so the claim stays
 * honest: one constant, updated when the library is re-checked.
 */

export type ContentSource = {
  label: string;
  url: string;
  note?: string;
};

/** ISO date the easement content library was last reviewed end to end. */
export const CONTENT_REVIEWED_ON = "2026-10-01";

/** Human-readable form of {@link CONTENT_REVIEWED_ON}. */
export function formatReviewDate(iso: string = CONTENT_REVIEWED_ON): string {
  const parts = iso.split("-").map(Number);
  const date = new Date(Date.UTC(parts[0] ?? 1970, (parts[1] ?? 1) - 1, parts[2] ?? 1));
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Who reviews the content. */
export const CONTENT_REVIEWER = "Easements.com";

/** One-line summary of how the material is sourced. */
export const SOURCING_LINE =
  "Written from state statutes, county recorder publications, and standard appraisal practice. Every state-specific figure links to a free official source you can open yourself.";

/** Sources cited across the whole library, listed on the editorial page. */
export const LIBRARY_SOURCES: ContentSource[] = [
  {
    label: "State legislature and statute portals",
    url: "https://www.congress.gov/state-legislature-websites",
    note: "Prescriptive periods, recording requirements and abandonment rules are quoted from each state's own published code.",
  },
  {
    label: "County recorder, clerk and register of deeds offices",
    url: "https://www.naco.org/counties",
    note: "Filing steps, index coverage and copy procedures come from each office's published guidance.",
  },
  {
    label: "Uniform Standards of Professional Appraisal Practice (USPAP)",
    url: "https://www.appraisalfoundation.org/imis/TAF/Standards/Appraisal_Standards/Uniform_Standards_of_Professional_Appraisal_Practice/TAF/USPAP.aspx",
    note: "The before-and-after framework used by the valuation calculator follows standard partial-acquisition appraisal method.",
  },
  {
    label: "Federal Highway Administration right-of-way guidance",
    url: "https://www.fhwa.dot.gov/real_estate/",
    note: "Public-agency practice for corridor acquisition, damages and remainder effects.",
  },
  {
    label: "USDA NRCS conservation easement programs",
    url: "https://www.nrcs.usda.gov/programs-initiatives/acep-agricultural-conservation-easement-program",
    note: "Terms, term lengths and appraisal expectations for conservation easements.",
  },
];

/** Editorial rules stated publicly so readers can hold us to them. */
export const EDITORIAL_RULES: string[] = [
  "We cite the primary source. Where a page states a statutory period, a recording requirement, or a filing procedure, it links to the state code section or the county office that publishes it.",
  "We do not publish dollar figures that go stale. Recording fees change without notice, so county pages point at the office's current fee schedule instead of quoting a number that will be wrong next year.",
  "We do not invent people, firms, reviews or credentials. Directory listings are only published after a human reviews the application and checks the license number given.",
  "We label estimates as estimates. The valuation calculator shows its inputs, its math and its assumptions, and says plainly where an appraiser's judgment would replace a default.",
  "We write general information, not legal advice for your situation. Easement outcomes turn on the exact recorded wording and on state law, and we say so on every page that touches a legal question.",
  "We correct errors in public. If you find something wrong, tell us what page and what source contradicts it and we will fix or remove it.",
];
